import { ActivityIndicator } from 'components/BlockingLoader/ActivityIndicator';
import { Icon, ICONS } from 'components/Icon';
import { Pressable } from 'components/Pressable';
import { Image, ImageContentFit } from 'expo-image';
import * as React from 'react';
import {
  StyleSheet,
  StyleProp,
  View,
  ViewStyle,
  DimensionValue,
} from 'react-native';

type Props = {
  imageUrl?: string;
  width: DimensionValue;
  height: DimensionValue;
  icon?: ICONS;
  style?: StyleProp<ViewStyle>;
  contentFit?: ImageContentFit;
  iconSize?: number;
  onPress?: () => void;
} & (
  | {
      rounded?: boolean;
      borderRadius?: undefined;
    }
  | {
      rounded?: undefined;
      borderRadius: number;
    }
);

export const CloudThumbnail: React.FC<Props> = React.memo(
  ({
    icon = ICONS.ImageThin,
    imageUrl,
    rounded = false,
    borderRadius,
    contentFit = 'contain',
    width,
    height,
    iconSize,
    style,
    onPress,
  }) => {
    const [state, setState] = React.useState<{
      status: 'loading' | 'loaded' | 'noimage' | 'errored';
      url: string;
    }>({
      status: imageUrl ? 'loading' : 'noimage',
      url: imageUrl || '',
    });

    React.useEffect(() => {
      if (!imageUrl) {
        setState({ status: 'noimage', url: '' });
      } else if (state.url !== imageUrl) {
        setState({
          status: 'loading',
          url: imageUrl || '',
        });
      }
    }, [imageUrl, state.url]);

    const ImageMemo = React.useMemo(() => {
      return (
        <Image
          style={{
            width: '100%',
            height: '100%',
          }}
          contentFit={contentFit}
          contentPosition="center"
          onLoad={() =>
            setState((s) => ({
              ...s,
              status: 'loaded',
            }))
          }
          onError={() => setState((s) => ({ ...s, status: 'errored' }))}
          key={state.url}
          source={{
            uri: state.url,
          }}
        />
      );
    }, [contentFit, state.url]);

    const widthInt: number = Number.isInteger(width) ? (width as number) : 0;
    const heightInt: number = Number.isInteger(height) ? (height as number) : 0;
    const getIconSize = React.useCallback(
      (dimension: number) => Math.round(dimension * 0.6),
      [],
    );

    return (
      <Pressable
        style={[
          styles.container,
          {
            borderRadius: rounded ? 9999 : borderRadius,
            width: width || '100%',
            height: width || '100%',
            overflow: 'hidden',
          },
          style,
        ]}
        onPress={onPress && imageUrl ? onPress : undefined}
      >
        {state.status === 'loading' && (
          <View
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              right: 0,
              bottom: 0,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ActivityIndicator size={30} />
          </View>
        )}
        {state.status === 'loading' || state.status === 'loaded' ? (
          ImageMemo
        ) : (
          <Icon
            source={icon}
            size={
              iconSize ||
              (widthInt && heightInt
                ? getIconSize(Math.min(widthInt, heightInt))
                : widthInt
                  ? getIconSize(widthInt)
                  : heightInt
                    ? getIconSize(heightInt)
                    : getIconSize(80))
            }
            color="#bbb"
          />
        )}
      </Pressable>
    );
  },
);

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#ddd',
    alignItems: 'center',
    justifyContent: 'center',
  },
});
